// src/pages/admin/AdminDashboardPage.tsx
// ============================================================
// Summit League v2 — Admin Dashboard (Phase E)
//
// Full dashboard with real stats from /api/admin/dashboard/stats.
// Clickable stat cards navigate to their respective pages.
// ============================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';

import '../../styles/admin-dashboard.css';

interface DashboardStats {
  openDisputes: number;
  totalUsers: number;
  activeUsers: number;
  bannedUsers: number;
  activeTeams: number;
  matchesToday: number;
  matchesThisWeek: number;
  publishedNews: number;
}

const EMPTY_STATS: DashboardStats = {
  openDisputes: 0,
  totalUsers: 0,
  activeUsers: 0,
  bannedUsers: 0,
  activeTeams: 0,
  matchesToday: 0,
  matchesThisWeek: 0,
  publishedNews: 0,
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>(EMPTY_STATS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axiosClient.get('/admin/dashboard/stats');
        setStats(res.data.data);
      } catch (err: any) {
        setError('Some stats may be unavailable');
        try {
          const disputeRes = await axiosClient.get('/disputes/count');
          setStats((prev) => ({
            ...prev,
            openDisputes: disputeRes.data?.openDisputes || 0,
          }));
        } catch {
          // Silent fail
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const val = (n: number) => (isLoading ? '—' : n.toLocaleString());

  return (
    <div className="sl-admin-dashboard">
      <div className="sl-admin-page-header">
        <h1 className="sl-admin-page-title">Dashboard</h1>
        <p className="sl-admin-page-subtitle">Platform overview</p>
      </div>

      {error && <div className="sl-admin-error sl-admin-error--soft">{error}</div>}

      {/* Primary Stats Row */}
      <div className="sl-admin-stats-grid">
        <button
          type="button"
          className={`sl-admin-stat-card ${stats.openDisputes > 0 ? 'sl-admin-stat-card--alert' : ''}`}
          onClick={() => navigate('/admin/disputes')}
        >
          <div className="sl-admin-stat-icon">⚠️</div>
          <div className="sl-admin-stat-content">
            <span className="sl-admin-stat-value">{val(stats.openDisputes)}</span>
            <span className="sl-admin-stat-label">Open Disputes</span>
          </div>
        </button>

        <button
          type="button"
          className="sl-admin-stat-card"
          onClick={() => navigate('/admin/users')}
        >
          <div className="sl-admin-stat-icon">👥</div>
          <div className="sl-admin-stat-content">
            <span className="sl-admin-stat-value">{val(stats.totalUsers)}</span>
            <span className="sl-admin-stat-label">Total Users</span>
          </div>
        </button>

        <button
          type="button"
          className="sl-admin-stat-card"
          onClick={() => navigate('/admin/users?status=active')}
        >
          <div className="sl-admin-stat-icon">🏆</div>
          <div className="sl-admin-stat-content">
            <span className="sl-admin-stat-value">{val(stats.activeTeams)}</span>
            <span className="sl-admin-stat-label">Active Teams</span>
          </div>
        </button>

        <div className="sl-admin-stat-card">
          <div className="sl-admin-stat-icon">🎮</div>
          <div className="sl-admin-stat-content">
            <span className="sl-admin-stat-value">{val(stats.matchesToday)}</span>
            <span className="sl-admin-stat-label">Matches Today</span>
          </div>
        </div>
      </div>

      {/* Secondary Stats Row */}
      <div className="sl-admin-stats-grid sl-admin-stats-grid--secondary">
        <div className="sl-admin-stat-card sl-admin-stat-card--compact">
          <div className="sl-admin-stat-content">
            <span className="sl-admin-stat-value">{val(stats.matchesThisWeek)}</span>
            <span className="sl-admin-stat-label">Matches This Week</span>
          </div>
        </div>

        <div className="sl-admin-stat-card sl-admin-stat-card--compact">
          <div className="sl-admin-stat-content">
            <span className="sl-admin-stat-value">{val(stats.activeUsers)}</span>
            <span className="sl-admin-stat-label">Active Users</span>
          </div>
        </div>

        {stats.bannedUsers > 0 && (
          <button
            type="button"
            className="sl-admin-stat-card sl-admin-stat-card--compact sl-admin-stat-card--alert-soft"
            onClick={() => navigate('/admin/users?status=banned')}
          >
            <div className="sl-admin-stat-content">
              <span className="sl-admin-stat-value">{val(stats.bannedUsers)}</span>
              <span className="sl-admin-stat-label">Banned Users</span>
            </div>
          </button>
        )}

        <button
          type="button"
          className="sl-admin-stat-card sl-admin-stat-card--compact"
          onClick={() => navigate('/admin/news')}
        >
          <div className="sl-admin-stat-content">
            <span className="sl-admin-stat-value">{val(stats.publishedNews)}</span>
            <span className="sl-admin-stat-label">Published Posts</span>
          </div>
        </button>
      </div>
    </div>
  );
}
